// Song URL. A YouTube analysis lives at /watch?v=<id> — the same path and id as
// youtube.com/watch?v=…, so swapping the domain of a YouTube link opens its chord sheet. Everything
// else (home, uploaded files) is /. Old song-page addresses (/chords/<id>, /tabs/<id>, /en|ja/…)
// and the old /?v=<id> deep link still resolve to the video.

import type { SongSource } from "../types/domain";
import { clampSetting, type KeySetting } from "./theory/transpose";

const VID_RE = /^[A-Za-z0-9_-]{11}$/;
const OLD_PATH_RE = /^\/(?:(?:en|ja)\/)?(?:chords|tabs)\/([A-Za-z0-9_-]{11})\/?$/;

/** video id addressed by a location (pathname + search), or null */
export function videoIdFromLocation(pathname: string, search: string): string | null {
  const old = OLD_PATH_RE.exec(pathname);
  if (old) return old[1];
  if (pathname !== "/" && pathname !== "/watch") return null;
  const v = new URLSearchParams(search).get("v");
  return v && VID_RE.test(v) ? v : null;
}

/** open a YouTube song from elsewhere in the app: same path as following a link (SongInput loads it) */
export function openSong(videoId: string): void {
  history.pushState(null, "", `/watch?v=${videoId}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/** canonical app path for what is loaded */
export function songPath(source: SongSource): string {
  return source.kind === "youtube" ? `/watch?v=${source.videoId}` : "/";
}

/** key/capo carried by a song link (…&transpose=2&capo=4); null when the link carries neither */
export function keyFromSearch(search: string): KeySetting | null {
  const q = new URLSearchParams(search);
  if (!q.has("transpose") && !q.has("capo")) return null;
  return clampSetting({ transpose: Number(q.get("transpose") ?? 0), capo: Number(q.get("capo") ?? 0) });
}

/** songPath plus a non-default key/capo, so a shared link opens the same way (YouTube songs only) */
export function songPathWithKey(source: SongSource, key: KeySetting): string {
  const base = songPath(source);
  if (source.kind !== "youtube") return base;
  return base + (key.transpose ? `&transpose=${key.transpose}` : "") + (key.capo ? `&capo=${key.capo}` : "");
}
